// ─── /app/p/{code}-{short} — la página de un proyecto (ADR 0039 D9, GHE #1200) ───
// Same loader flow as /app/l/$listRef (ADR 0012 D3): parse the ref → resolve the short id inside
// the caller's visible set → 404 when it is not there → canonical redirect when the slug drifted
// (a renamed code self-heals). The page itself reads the project by id.

import { createFileRoute, notFound, redirect } from '@tanstack/react-router';
import { z } from 'zod';
import { parseEntityRef } from '@a8-planner/shared/utils/entity-url';
import type { ProjectResolved } from '@a8-planner/shared';
import { projectsApi } from '../../../lib/api/projects';
import { ApiError } from '../../../lib/api-client';
import { ProjectPage } from '../../../features/projects/ProjectPage';

export const PROJECT_TABS = [
  'resumen',
  'etapas',
  'cronograma',
  'hitos',
  'registro',
  'ajustes',
] as const;

const projectSearchSchema = z.object({
  // An unknown or missing tab is the summary, never a crash (a hand-edited URL is still a URL).
  tab: z.enum(PROJECT_TABS).optional().catch(undefined),
});

export const Route = createFileRoute('/app/p/$projectRef')({
  validateSearch: projectSearchSchema,
  loader: async ({ params }): Promise<ProjectResolved> => {
    const parsed = parseEntityRef(params.projectRef);
    if (!parsed) throw notFound();

    let resolved: ProjectResolved;
    try {
      resolved = (await projectsApi.resolve(parsed.short)).data;
    } catch (err) {
      // Out of scope and nonexistent are the same answer by design: never a 403 that confirms it.
      if (err instanceof ApiError && err.status === 404) throw notFound();
      if (err instanceof ApiError && err.status === 401) {
        throw redirect({
          to: '/login',
          search: { returnTo: window.location.pathname + window.location.search },
          replace: true,
        });
      }
      throw err;
    }

    if (parsed.slug !== resolved.canonicalSlug) {
      throw redirect({
        to: resolved.canonicalPath as '/app',
        search: (prev) => prev,
        replace: true,
      });
    }
    return resolved;
  },
  component: ProjectRoute,
});

function ProjectRoute(): React.ReactElement {
  const resolved = Route.useLoaderData();
  const { tab } = Route.useSearch();
  const navigate = Route.useNavigate();
  return (
    <ProjectPage
      projectId={resolved.id}
      tab={tab ?? 'resumen'}
      onTabChange={(next) =>
        void navigate({ search: { tab: next === 'resumen' ? undefined : next }, replace: true })
      }
    />
  );
}
